/**
 * TypeScript view of the `masyu` format.
 * Source of truth: `shared/schema/masyu.{definition,submission,solution,progress}.json`.
 *
 * The loop runs through cell centres. `h[r][c]` joins cell (r, c) to (r, c + 1) (`rows`
 * strings of `cols - 1`); `v[r][c]` joins cell (r, c) to (r + 1, c) (`rows - 1` strings of
 * `cols`).
 */

export type Localized = Record<string, string | undefined>;

export interface MasyuDefinition {
  puzzleType: "masyu";
  typeVersion: number;
  rows: number;
  cols: number;
  /** One string per row: `w` a white pearl, `b` a black one, `.` none. */
  pearls: string[];
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

export const LINE = "#";
export const NONE = ".";
/** A solving aid: the player is sure the segment stays empty. Never sent to the server. */
export const CROSSED = "x";
export const WHITE = "w";
export const BLACK = "b";
export const NO_PEARL = ".";
export type EdgeState = typeof LINE | typeof NONE | typeof CROSSED;
export type Pearl = typeof WHITE | typeof BLACK | typeof NO_PEARL;

export interface Edges {
  /** `rows` strings of `cols - 1` characters. */
  h: string[];
  /** `rows - 1` strings of `cols` characters. */
  v: string[];
}

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface MasyuProgress extends Edges {
  version: 1;
}

/** `#` a segment and `.` none: crosses are folded into `.`. */
export type MasyuSubmission = Edges;

/** The author's loop, in the submission format. */
export type MasyuSolution = Edges;

export const MIN_SIZE = 2;
export const MAX_SIZE = 30;

export type Axis = "h" | "v";
export interface Edge {
  axis: Axis;
  r: number;
  c: number;
}
export type Cell = [row: number, col: number];

export function edgeKey(edge: Edge): string {
  return `${edge.axis}:${edge.r}:${edge.c}`;
}

export function cellKey([r, c]: Cell): string {
  return `${r},${c}`;
}

export function cellId(row: number, col: number): string {
  return `r${row}c${col}`;
}

/** Picks the viewer's language, falling back to the base language, then English, then any. */
export function localized(value: Localized | null | undefined, locale: string): string {
  if (!value) return "";
  return (
    value[locale] ??
    value[locale.split("-")[0]] ??
    value.en ??
    Object.values(value).find((v) => typeof v === "string" && v.length > 0) ??
    ""
  );
}
